import { InfoCard } from '@/components/info-card';
import { Button, buttonVariants } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FormSchema } from '@/lib/form-utils';

import type { VariantProps } from 'class-variance-authority';
import { zodResolver } from '@hookform/resolvers/zod';
import { ComponentProps, ReactNode, useState } from 'react';
import { DefaultValues, FieldValues, UseFormReturn, useForm } from 'react-hook-form';

import { z } from 'zod';

type EditableInfoCardButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

export interface SubmitProps<FormData extends FieldValues> {
  formData: FormData;
  disableEditing: () => void;
  form: UseFormReturn<FormData>;
}

interface EditableInfoCardProps<S extends FormSchema> extends Omit<ComponentProps<typeof InfoCard>, 'children'> {
  defaultValues?: DefaultValues<z.infer<S>>;
  onSubmit?: (submitProps: SubmitProps<z.infer<S>>) => unknown;
  children: (props: { form: UseFormReturn<z.infer<S>>; editing: boolean }) => ReactNode;
  formSchema: S;
  saveButtonLabel?: string;
  editButtonLabel?: string;
  readonly?: boolean;
  shouldResetOnCancel?: boolean;
  customSubmitActions?: (props: { form: UseFormReturn<z.infer<S>>; editing: boolean }) => ReactNode;
  defaultEditing?: boolean;
  secondaryActionSection?: (props: { form: UseFormReturn<z.infer<S>>; editing: boolean }) => ReactNode;
  editButtonVariant?: EditableInfoCardButtonVariant;
  saveButtonVariant?: EditableInfoCardButtonVariant;
  cancelButtonVariant?: EditableInfoCardButtonVariant;
}

export function EditableInfoCard<S extends FormSchema>({
  children,
  headerActions = <></>,
  defaultValues,
  onSubmit,
  formSchema,
  readonly,
  saveButtonLabel = 'Save',
  editButtonLabel = 'Edit',
  shouldResetOnCancel = true,
  customSubmitActions,
  secondaryActionSection,
  defaultEditing = false,
  editButtonVariant = 'destructive',
  saveButtonVariant = 'destructive',
  cancelButtonVariant = 'dismiss',
  ...otherInfoCardProps
}: EditableInfoCardProps<S>) {
  const [editing, setEditing] = useState(defaultEditing);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { isSubmitting } = form.formState;
  const dataTestId =
    'data-testid' in otherInfoCardProps ? (otherInfoCardProps['data-testid'] as string | undefined) : undefined;

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((formData) => {
            return onSubmit?.({
              form,
              formData,
              disableEditing: () => setEditing(false),
            });
          })}
        >
          <InfoCard
            {...otherInfoCardProps}
            headerActions={
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  {headerActions}

                  {!readonly && (
                    <>
                      {editing && (
                        <Button
                          onClick={() => {
                            if (shouldResetOnCancel) {
                              form.reset();
                            }
                            setEditing(false);
                          }}
                          size="md"
                          variant={cancelButtonVariant}
                          loading={isSubmitting}
                          data-testid={dataTestId ? `${dataTestId}-button-cancel` : undefined}
                        >
                          Cancel
                        </Button>
                      )}

                      {editing &&
                        (customSubmitActions ? (
                          customSubmitActions({ form, editing: readonly ? false : editing })
                        ) : (
                          <Button
                            type="submit"
                            loading={isSubmitting}
                            size="md"
                            variant={saveButtonVariant}
                            data-testid={dataTestId ? `${dataTestId}-button-submit` : undefined}
                          >
                            {saveButtonLabel}
                          </Button>
                        ))}

                      {!editing && (
                        <Button
                          onClick={() => setEditing(true)}
                          size="md"
                          variant={editButtonVariant}
                          data-testid={dataTestId ? `${dataTestId}-button-edit` : 'editable-info-card-button-edit'}
                        >
                          {editButtonLabel}
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {secondaryActionSection && (
                  <div className="flex justify-end">
                    {secondaryActionSection({ form, editing: readonly ? false : editing })}
                  </div>
                )}
              </div>
            }
          >
            <>{children({ form, editing: readonly ? false : editing })}</>
          </InfoCard>
        </form>
      </Form>
    </>
  );
}
